import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MARKET, PLAN, TMarket, TPlan } from '@/common/constants/markets';
import { queryClient } from '@/common/contexts/react-query/ReactQueryProvider';
import { useMarket } from '@/common/hooks/useMarket';
import { IMenhirBenefitSlug } from '@/common/integrations/menhir/employee/benefits/benefits.types';
import { budgetsSummaryQueryOptions } from '@/common/integrations/menhir/employee/budgets/budgets.hooks';
import { ewaDetailsQueryOptions } from '@/common/integrations/menhir/employee/salary/salary.hooks';
import { formatDateISO, now } from '@/common/utils/date';
import { endOfYear, set, startOfYear } from 'date-fns';

export type TActivityProductType = 'meal' | 'benefits' | 'budgets' | 'ewa';

export type TActivityFilters = {
  from: string;
  to: string;
  product?: TActivityProductType;
  benefit?: IMenhirBenefitSlug;
  budget?: string;
};

export function getYearFilter(year: number | undefined) {
  return {
    from: formatDateISO(startOfYear(set(now(), { year }))),
    to: formatDateISO(endOfYear(set(now(), { year }))),
  };
}

export function getYearsOptions() {
  const options = [];
  let currentYear = now().getFullYear();
  while (currentYear >= 2020) {
    options.push({
      label: `${currentYear}`,
      value: `${currentYear}`,
    });
    currentYear--;
  }
  return options;
}

export function getInitialActivityFilters(
  initialParams: Pick<TActivityFilters, 'product' | 'benefit' | 'budget'>
) {
  return {
    from: formatDateISO(startOfYear(set(now(), { year: now().getFullYear() }))),
    to: formatDateISO(endOfYear(set(now(), { year: now().getFullYear() }))),
    ...initialParams,
  };
}

export function useGetProductOptions() {
  const { t } = useTranslation();
  const { slug, plan } = useMarket();
  const [productOptions, setProductOptions] = useState<TActivityProductType[]>(
    []
  );

  useEffect(() => {
    getProductOptionsForMarket(slug, plan).then((products) => {
      setProductOptions(products);
    });
  }, [slug, plan]);

  return productOptions.map((option) => ({
    label: t(getProductLabelKeyForMarket(option, slug)),
    value: option,
  }));
}

function getProductLabelKeyForMarket(
  product: TActivityProductType,
  market: TMarket
) {
  switch (market) {
    case MARKET.IT:
      if (product === 'benefits') {
        return 'general.products.welfare';
      }
      if (product === 'meal') {
        return 'general.products.buoni-pasto';
      }
      return `general.products.${product}`;
    default:
      return `general.products.${product}`;
  }
}
async function getProductOptionsForMarket(
  market: TMarket,
  plan: TPlan
): Promise<TActivityProductType[]> {
  switch (market) {
    case MARKET.PT:
      if (plan === PLAN.SUC_MEAL) {
        return ['meal'];
      }
      const additionalProducts = await getAdditionalProductsForWallet();
      return ['meal', 'benefits', ...additionalProducts];
    case MARKET.IT:
      if (plan === PLAN.ITALY) {
        return ['meal'];
      }
      if (plan === PLAN.ITALY_FRINGE) {
        return ['benefits'];
      }
      return ['meal', 'benefits'];
    case MARKET.ES:
      if (plan === PLAN.ES_MEAL) {
        return ['meal'];
      }
      return ['meal', 'benefits'];
    default:
      return [];
  }
}

async function getAdditionalProductsForWallet() {
  const additionalProducts: TActivityProductType[] = [];
  try {
    await queryClient.fetchQuery(budgetsSummaryQueryOptions);
    additionalProducts.push('budgets');
  } catch {}

  try {
    await queryClient.fetchQuery(ewaDetailsQueryOptions);
    additionalProducts.push('ewa');
  } catch {}

  return additionalProducts;
}

export function getNumberOfActiveFilters(filters: TActivityFilters) {
  return Object.values(filters).filter(Boolean).length - 1;
}
